<template>
    <card :class="cardCssClass" :style="stickyQuotePositionStyle">
        <template #default>
            <div v-if="showHeader" class="d-flex position-relative" :class="cardHeaderCssClass" @click="toggleShowRates">
                <card-image v-if="displayImage" :src="displayImage" :background="true" :width="displayImageWidth" class="flex-shrink-0" :class="displayImageCssClass" />
                <card-body class="d-md-flex d-lg-block justify-content-between align-items-center" :style="`width:${displayBodyWidth};`">
                    <div>
                        <div class="text-uppercase mb-1 mb-md-2 x-small opacity-75">
                            {{ property.city }}, {{ property.state }} {{ (property.country && (property.country != 'US')) ? property.country : '' }}
                        </div>
                        <h6 v-if="uiStore.isMobile" class="text-truncate">{{ property.name }}</h6>
                        <h5 v-else>{{ property.name }}</h5>
                    </div>
                    <div v-if="!showRates && includeBookButton && (uiStore.isMobile || uiStore.isTablet)" class="d-md-flex align-items-end justify-content-between" :class="uiStore.isTablet ? 'text-nowrap' : null">
                        <div class="mt-2 mt-md-3 mt-lg-0 ps-md-4">
                            <div class="text-primary small mb-1">
                                <i class="far fa-calendar-alt"></i> &nbsp; {{ checkIn.format('D, n/j/Y') }} - {{ checkOut.format('D, n/j/Y') }}
                            </div>
                            <div>
                                Grand Total: <strong>{{ money(quote.total) }}</strong>
                            </div>
                            <div v-if="showDeposit && quote.deposit" class="x-small text-muted">
                                + refundable security deposit of {{ money(quote.deposit) }}
                            </div>
                        </div>
                        <div @click.stop class="mt-2 mt-md-0 ms-md-3 md-lg-0">
                            <ui-button @click="$emit('book')" size="sm" block="sm">
                                {{ bookButtonLabel }} 
                            </ui-button>
                        </div>
                    </div>
                    <div v-else-if="!showRates" class="mt-2 mt-md-3 mt-lg-0 ps-md-4">
                        Grand Total: <strong>{{ money(quote.total) }}</strong>
                        <div v-if="(uiStore.isMobile || uiStore.isTablet) && showDeposit && quote.deposit" class="x-small text-muted">
                            + refundable security deposit of {{ money(quote.deposit) }}
                        </div>
                    </div>

                    <div v-if="uiStore.isMobile || uiStore.isTablet" class="clickable position-absolute top-0 end-0 p-2">
                        <badge color="light" :class="isAnimating ? 'shack-css-animation' : null">
                            <i class="fas fa-chevron-down" v-if="showRates"></i>
                            <i class="fas fa-chevron-up" v-else></i>
                        </badge>
                    </div>

                </card-body>
            </div>
            <transition name="v-slide-transition-4">
                <div v-if="showRates">
                    <card-body>
                        <div class="text-primary small">
                            <i class="far fa-calendar-alt"></i> &nbsp; {{ checkIn.format('D, n/j/Y') }} - {{ checkOut.format('D, n/j/Y') }}
                        </div>

                        <hr class="mb-0" />

                        <quote-fees-list :property="property" :quote="quote" class="text-muted" />

                    </card-body>
                    <card-body class="bg-info" :class="grandTotalCssClass">

                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <strong>GRAND TOTAL</strong>
                            </div>
                            <div class="display-5">{{ money(quote.total) }}</div>
                        </div>
                        
                        <div v-if="includeBookButton" class="text-center">
                            <ui-button class="mt-3" :block="!uiStore.isTablet" @click="$emit('book')" :loading="buttonLoading" :disabled="buttonLoading">
                                {{ bookButtonLabel }} 
                            </ui-button>
                        </div>

                    </card-body>
                </div>
            </transition>
        </template>
        <template #footer v-if="showDeposit && quote.deposit && showRates">
            <div class="x-small text-muted">
                Plus a refundable security deposit of {{ money(quote.deposit) }}
            </div>
        </template>
        <template #footer v-else-if="showDeposit && depositNoteAmount && showRates">
            <div class="x-small text-muted">
                A refundable security deposit of {{ money(depositNoteAmount) }} will be due to the owner before check-in. The amount of the deposit is not included in the Grand Total shown above.
            </div>
        </template>
    </card>
</template>

<script type="text/javascript">
    import QuoteFeesList from '@/components/Content/Public/Properties/QuoteFeesList.vue';
    import CommonComponents from '@/components/Common.vue';
    import ApiObject from '@/classes/ApiObject.js';
    import DateObject from '@/classes/DateObject.js';
    import Formatter from '@/classes/Formatter.js';
    import MobileApp from '@/classes/MobileApp.js';

    import { UseUIStore } from '@/store/UI.js';

    export default {
        components: {
            QuoteFeesList,
            ...CommonComponents,
        },

        setup() {
            return {
                uiStore: UseUIStore(),
            };
        },

        data() {
            return {
                clickedShowRate: false,

                stopAnimation: false,
                isAnimating: false,
            };
        },

        emits: ['book'],

        props: {
            property: {
                type: ApiObject,
                required: true,
            },

            offer: {
                type: ApiObject,
            },

            checkIn: {
                type: DateObject,
                required: true,
            },

            checkOut: {
                type: DateObject,
                required: true,
            },

            quote: {
                type: Object,
                required: true,
            },

            showAnimation: {
                type: Boolean,
                default: false,
            },

            stickyTop: {
                type: Boolean,
                default: false,
            },

            includeBookButton: {
                type: Boolean,
                default: false,
            },

            bookButtonLabel: {
                type: String,
                default: 'Book Now',
            },

            buttonLoading: {
                type: Boolean,
                default: false,
            },

            showHeader: {
                type: Boolean,
                default: true,
            },

            showInFull: {
                type: Boolean,
                default: false,
            },

            showDeposit: {
                type: Boolean,
                default: true,
            },
        },

        mounted() {
            let didShowAnimation = MobileApp.getSessionItem('publicAnimateQuoteBox');

            if (
                (
                    (this.uiStore.isMobile) ||
                    (this.uiStore.isTablet)
                ) &&
                (!didShowAnimation) &&
                (this.showAnimation)
            ) {

                MobileApp.setSessionItem('publicAnimateQuoteBox', true);
                
                setTimeout(function() {
                    this.isAnimating = true;

                    setTimeout(function() {

                        if (!this.isAnimating) return;

                        this.clickedShowRate = true;

                        setTimeout(function() {

                            if (!this.isAnimating) return;

                            this.clickedShowRate = false;
                            this.isAnimating = false;

                        }.bind(this), 3000);

                    }.bind(this), 1000);

                }.bind(this), 500);
            }
        },

        computed: {
            depositNoteAmount() {
                if (
                    (this.quote) &&
                    (this.quote.deposit)
                ) {
                    return null;
                }

                if (
                    (this.offer) &&
                    (this.offer.depositNoteAmount)
                ) {
                    return this.offer.depositNoteAmount;
                }

                if (
                    (this.property) &&
                    (this.property.deposit) &&
                    (this.property.deposit.amount)
                ) {
                    return this.property.deposit.amount;
                }

                return null;
            },

            showRates() {
                if (this.showInFull) {
                    return true;
                }
                if (
                    (this.uiStore.isMobile) ||
                    (this.uiStore.isTablet)
                ) {
                    return this.clickedShowRate;
                }
                return true;
            },

            stickyQuotePositionStyle() {
                if (!this.stickyTop) return null;

                if ((!this.uiStore.isMobile) && (!this.uiStore.isTablet)) {

                    let top = 50;
                    if (this.uiStore.stickTopNavTop) {
                        top += this.uiStore.stickTopNavTop;
                        
                    }
                    return 'top: '+top+'px;';
                }
                return null;
            },

            cardCssClass() {
                if (
                    (!this.showInFull) &&
                    (
                        (this.uiStore.isMobile) ||
                        (this.uiStore.isTablet)
                    )
                ) {
                    return 'fixed-bottom border-top rounded-top shadow border-dark overflow-hidden rounded-bottom-0 border-bottom-0'
                }
                else if (this.stickyTop) {
                    return 'sticky-top mt-3';
                }
                return null;
            },

            cardHeaderCssClass() {
                if (
                    (this.uiStore.isMobile) ||
                    (this.uiStore.isTablet)
                ) {
                    if (this.showRates) {
                        return 'bg-info clickable';
                    }

                    return 'bg-info clickable';
                }
                return 'bg-light';
            },

            displayImage() {
                if (this.property) {
                    let photo;
                    if (
                        (this.property.firstPhoto) &&
                        (this.property.firstPhoto.cdnImages)
                    ) {
                        photo = this.property.firstPhoto;
                        
                    }
                    else if (
                        (this.property.photos) &&
                        (this.property.photos.length)
                    ) {
                        photo = this.property.photos[0];
                    }

                    if (photo) {
                        if (photo.galleryThumbSource) {
                            return photo.galleryThumbSource;
                        }

                        if (photo.cdnImages) {
                            let sizes = ['single', 'singleHeader', 'homeaway'];
                            for (let i=0; i<sizes.length; i++) {
                                if (photo.cdnImages[sizes[i]]) {
                                    return photo.cdnImages[sizes[i]];
                                }
                            }
                        }
                    }

                    if ((this.property.photosUrlList) && (this.property.photosUrlList.length)) {
                        return this.property.photosUrlList[0];
                    }
                }


                return null;
            },

            displayImageWidth() {
                if (
                    (this.uiStore.isMobile) ||
                    (this.uiStore.isTablet)
                ) {
                    return '25%';
                }
                return '35%';
            },

            displayBodyWidth() {
                if (
                    (this.uiStore.isMobile) ||
                    (this.uiStore.isTablet)
                ) {
                    return '75%';
                }
                return '65%';
            },

            displayImageCssClass() {
                if (
                    (this.uiStore.isMobile) ||
                    (this.uiStore.isTablet)
                ) {
                    return 'rounded-0';
                }
                return 'rounded-end-0';
            },

            grandTotalCssClass() {
                if (
                    (this.uiStore.isMobile) ||
                    (this.uiStore.isTablet)
                ) {
                    return null;
                }

                if (
                    (this.showRates) &&
                    (
                        (this.depositNoteAmount) ||
                        (
                            (this.quote) &&
                            (this.quote.deposit)
                        )
                    )
                ) {
                    return null;
                }
                
                return 'rounded-bottom-2';
            },
        },

        methods: {
            money(amount) {
                return Formatter.money(amount);
            },

            toggleShowRates() {
                this.isAnimating = false;
                this.clickedShowRate = !this.clickedShowRate;
            },
        }
    };

</script>
